import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useMergeRefs } from "../../../../hooks";
import CloseIcon from "@servicetitan/hammer-icon/mdi/round/close.svg";
import { Filter } from "./internal/types";
import { FilterItemWrapper } from "./FilterItemWrapper";
import Button from "../../../../components/Button";
import { LayoutUtilProps } from "../../../../types";
import { useContainerQuery } from "../../../../internal/hooks";
import {
  checkActiveFilters,
  resetFilters,
  updateSingleFilter,
  hasFilterSelectionChanged,
  preserveFilterState,
} from "./internal/utils/filter-state";
import { FilterToggleButton } from "./FilterToggleButton";
import { FilterButton } from "./FilterButton";
import { FilterGroupContext } from "./internal/FilterGroupContext";
import { ToolbarControlGroup } from "../ToolbarControlGroup";
import { FilterDrawer } from "./FilterDrawer";

import { useToolbarSize } from "../internal/ToolbarContext";

import styles from "./Filters.module.scss";

/**
 * Props for the FilterGroup component
 *
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type FilterGroupProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    /** Array of filter objects */
    filters: Filter[];
    /** Callback function when filters change */
    onFilterChange?: (filters: Filter[]) => void;
    /** Whether the filters are submission is controlled via an apply button */
    controlledFiltering?: boolean;
  };

/**
 * FilterGroup component displays a group of filters with toggles and a clear button.
 * Also contains the FilterDrawer for additional filter options.
 *
 * Features:
 * - Renders filter toggles and popovers for all filter types
 * - Supports controlled and uncontrolled filtering
 * - Integrates with FilterDrawer for batch filter editing
 * - Provides a clear all filters button
 * - Handles overflow and hidden filters
 * - Shares filter state via context for child components
 * - Supports boolean, custom, single-select, multi-select, date, and date range filter types
 * - Preserves filter state when filter configuration changes
 *
 * @example
 * <FilterGroup
 *   filters={filters}
 *   onFilterChange={handleFilter}
 *   controlledFiltering={true}
 * />
 */
const FilterGroupElement = forwardRef<HTMLDivElement, FilterGroupProps>(
  (props, ref) => {
    const {
      filters: initialFilters,
      onFilterChange,
      controlledFiltering = false,
      ...rest
    } = props;

    const [filters, setFilters] = useState<Filter[]>(initialFilters);
    const [hiddenFilters, setHiddenFilters] = useState<Filter[]>([]);
    const filterGroupRef = useRef<HTMLDivElement>(null);
    const containerQuery = useContainerQuery(filterGroupRef);
    const toolbarSize = useToolbarSize();

    const showInlineFilters =
      containerQuery &&
      containerQuery.name !== "xs" &&
      containerQuery.name !== "sm";

    // Temporary override to test if filters show when forced to true
    // const showInlineFilters = true;

    // Update filters when props change
    useEffect(() => {
      setFilters((prevFilters) => {
        // Optimization: Create a Map for O(1) lookup instead of O(n) find() in map()
        const existingFiltersMap = new Map(prevFilters.map((f) => [f.id, f]));

        return initialFilters.map((newFilter: Filter) => {
          // O(1) lookup instead of O(n) find
          const existingFilter = existingFiltersMap.get(newFilter.id);

          // If no existing filter, use the new one as-is
          if (!existingFilter) {
            return newFilter;
          }

          // Check if the selection value has changed (by reference)
          // If it hasn't changed, preserve existing state; if it has, use the new filter
          if (!hasFilterSelectionChanged(existingFilter, newFilter)) {
            // Selection value reference hasn't changed - preserve the existing filter's state
            return preserveFilterState(newFilter, existingFilter);
          }

          // Selection value has changed - use the new filter
          return newFilter;
        });
      });
    }, [initialFilters]);

    /**
     * Updates filter state when a filter is changed
     *
     * @param filterId - ID of the filter to update
     * @param value - New value for the filter
     */
    const updateFilter = useCallback(
      (filterId: string, value: unknown, submit: boolean) => {
        setFilters((prevFilters) => {
          const updatedFilters = updateSingleFilter(
            prevFilters,
            filterId,
            value,
          );

          if (submit) {
            onFilterChange?.(updatedFilters);
          }

          return updatedFilters;
        });
      },
      [onFilterChange],
    );

    /**
     * Adds a filter to the list of hidden filters.
     *
     * @param filter - The filter to add to the hidden filters array
     */
    const addHiddenFilter = useCallback((filter: Filter) => {
      setHiddenFilters((prev) => [...prev, filter]);
    }, []);

    /**
     * Removes a filter from the list of hidden filters.
     *
     * @param filter - The filter to remove from the hidden filters array
     */
    const removeHiddenFilter = useCallback((filter: Filter) => {
      setHiddenFilters((prev) => prev.filter((f) => f.id !== filter.id));
    }, []);

    /**
     * Clears all active filters
     */
    const clearAllFilters = () => {
      const resetAllFilters = resetFilters(filters);
      setFilters(resetAllFilters);
      onFilterChange?.(resetAllFilters);

      // Since the clear button disappears after clearing, we need to set focus on the first focusable element in the filter group
      setTimeout(() => {
        if (filterGroupRef.current) {
          const firstFocusable = filterGroupRef.current.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ) as HTMLElement;

          if (firstFocusable) {
            firstFocusable.focus();
          }
        }
      }, 0);
    };

    const hasActiveFilters = checkActiveFilters(filters);

    // Create filter controls that are displayed in the group
    const filterGroupControls = filters.map((filter) => {
      switch (filter.type) {
        case "boolean":
          return (
            <FilterToggleButton
              key={filter.id}
              id={filter.id}
              checked={filter.checked}
              onClick={() => {
                updateFilter(filter.id, !filter.checked, true);
              }}
            >
              {filter.label}
            </FilterToggleButton>
          );
        case "singleSelect":
        case "multiSelect":
          return (
            <FilterButton
              key={filter.id}
              filter={filter}
              className={styles["filter-select-content"]}
            />
          );
        case "custom":
        case "date":
        case "dateRange":
          return <FilterButton key={filter.id} filter={filter} />;
        // TODO: Add cases for other filter types here as needed
        default:
          return null;
      }
    });

    const value = useMemo(
      () => ({
        filterGroupRef,
        filters,
        updateFilter,
        onFilterChange,
        controlledFiltering,
        hiddenFilters,
        addHiddenFilter,
        removeHiddenFilter,
      }),
      [
        filterGroupRef,
        filters,
        updateFilter,
        onFilterChange,
        controlledFiltering,
        hiddenFilters,
        addHiddenFilter,
        removeHiddenFilter,
      ],
    );

    return (
      <FilterGroupContext.Provider value={value}>
        <ToolbarControlGroup
          ref={useMergeRefs([filterGroupRef, ref])}
          data-anv="toolbar-filter-group"
          aria-label="Filter controls"
          {...rest}
        >
          {showInlineFilters && filterGroupControls}
          <FilterDrawer />

          {showInlineFilters && hasActiveFilters && (
            <FilterItemWrapper>
              <Button
                appearance="ghost"
                size={toolbarSize}
                className={styles["filter-button-trigger"]}
                icon={{
                  before: CloseIcon,
                }}
                onClick={clearAllFilters}
              >
                Clear Filters
              </Button>
            </FilterItemWrapper>
          )}
        </ToolbarControlGroup>
      </FilterGroupContext.Provider>
    );
  },
);

FilterGroupElement.displayName = "FilterGroup";

export const FilterGroup = FilterGroupElement;
